/* Step 7a (owner card "AZEX Step 7 Card V2", 29-Oct-2026) — the items of a sample in its evaluation (card A3):
   Tested (audited value) · Cannot be tested (reason; counts at its full book value — S19) · Not applicable (reason → a
   replacement drawn by the seed rule and recorded on both items — S18) · Anomaly (reason + the additional procedures,
   required; stays in the best estimate at actual — S20). Calls PUT /samples/{id}/items/{key}/result and
   POST /samples/{id}/items/{key}/replace (routes_sample_eval.py). */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate } from "@/lib/dt";
import { amt, fill, statusKey } from "@/pages/Sampling/evaluationFormat";

function ItemEditor({ sampleId, item, t, onSaved, onCancel, notASample }) {
  const [status, setStatus] = useState(item.status === "pending" ? "tested" : item.status);
  const [audited, setAudited] = useState(item.audited_value ?? "");
  const [reason, setReason] = useState(item.untestable_reason || "");
  const [naReason, setNaReason] = useState("");
  const an = item.anomaly || {};
  const [isAnomaly, setIsAnomaly] = useState(!!an.is_anomaly);
  const [anReason, setAnReason] = useState(an.reason || "");
  const [anProcs, setAnProcs] = useState(an.procedures || "");
  const [busy, setBusy] = useState(false);
  const key = encodeURIComponent(item.item_key);

  const save = async () => {
    setBusy(true);
    try {
      if (status === "not_applicable") {
        const r = await api.post(`/samples/${sampleId}/items/${key}/replace`, { reason: naReason });
        const rep = r.data?.replacement;
        toast.success(rep ? fill(t("s7a.panel.replacedToast"), { uid: rep.uid, bv: amt(rep.book_value) }) : t("s7a.panel.naNoReplacement"));
      } else {
        await api.put(`/samples/${sampleId}/items/${key}/result`, {
          status,
          audited_value: status === "tested" && audited !== "" ? Number(audited) : null,
          untestable_reason: status === "untestable" ? reason : null,
          anomaly: isAnomaly ? { is_anomaly: true, reason: anReason, procedures: anProcs } : { is_anomaly: false },
        });
        toast.success(t("s7a.panel.savedToast"));
      }
      onSaved();
    } catch (e) {
      toast.error(toErrMsg(e, t("s7a.panel.saveFailed")));
    } finally {
      setBusy(false);
    }
  };

  const radio = (v, label) => (
    <label className="inline-flex items-center gap-1">
      <input type="radio" name={`st-${item.item_key}`} checked={status === v} onChange={() => setStatus(v)}
             data-testid={`ev-status-${v}-${item.item_key}`} />
      {label}
    </label>
  );
  return (
    <div className="space-y-2 rounded border border-slate-300 bg-white p-2 text-xs dark:bg-slate-900" data-testid={`ev-editor-${item.item_key}`}>
      <div className="flex flex-wrap gap-3">
        {radio("tested", t("s7a.statusTested"))}
        {radio("untestable", t("s7a.statusUntestable"))}
        {radio("not_applicable", notASample ? t("s7a.statusNotApplicable") : t("s7a.panel.naAction"))}
      </div>
      {status === "tested" && (
        <label className="block">{t("s7a.panel.auditedValue")}
          <Input type="number" step="0.01" dir="ltr" className="mt-0.5 h-7 w-40" value={audited}
                 onChange={(e) => setAudited(e.target.value)} data-testid={`ev-audited-${item.item_key}`} />
        </label>
      )}
      {status === "untestable" && (
        <label className="block">{t("s7a.panel.untestableReason")}
          <Input className="mt-0.5 h-7" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`ev-untestable-reason-${item.item_key}`} />
          <span className="text-[10px] text-muted-foreground">{t("s7a.untestableText")}</span>
        </label>
      )}
      {status === "not_applicable" && (
        <label className="block">{t("s7a.panel.naReason")}
          <Input className="mt-0.5 h-7" value={naReason} onChange={(e) => setNaReason(e.target.value)} data-testid={`ev-na-reason-${item.item_key}`} />
          <span className="text-[10px] text-muted-foreground">{notASample ? t("s7a.panel.naNoReplacement") : t("s7a.panel.naRule")}</span>
        </label>
      )}
      {status !== "not_applicable" && (
        <div className="rounded border border-amber-200 bg-amber-50/50 p-2 dark:bg-amber-950/20">
          <label className="inline-flex items-center gap-1 font-medium">
            <input type="checkbox" checked={isAnomaly} onChange={(e) => setIsAnomaly(e.target.checked)} data-testid={`ev-anomaly-${item.item_key}`} />
            {t("s7a.panel.anomalyMark")}
          </label>
          {isAnomaly && (
            <div className="mt-1 space-y-1">
              <label className="block">{t("s7a.panel.anomalyReason")}
                <Input className="mt-0.5 h-7" value={anReason} onChange={(e) => setAnReason(e.target.value)} data-testid={`ev-anomaly-reason-${item.item_key}`} />
              </label>
              <label className="block">{t("s7a.panel.anomalyProcedures")}
                <textarea rows={2} className="mt-0.5 w-full rounded border px-2 py-1 dark:bg-slate-900" value={anProcs}
                          onChange={(e) => setAnProcs(e.target.value)} data-testid={`ev-anomaly-procs-${item.item_key}`} />
              </label>
              <div className="text-[10px] text-muted-foreground">{t("s7a.anomalyText")}</div>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={busy} data-testid={`ev-save-${item.item_key}`}>{t("s7a.panel.save")}</Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}>{t("s7a.panel.cancel")}</Button>
      </div>
    </div>
  );
}

export default function EvaluationItems({ sampleId, items, t, locked, onSaved, notASample }) {
  const colW = useResizableColumns({ storageKey: "sampling-evaluation-items", defaults: [110, 110, 130, 130, 100, 110, 130, 130, 130, 130], mins: [72, 72, 96, 90, 64, 72, 90, 90, 90, 90] });   // Step 11d · add-on
  const [open, setOpen] = useState("");
  const uidOf = (k) => (items.find((i) => i.item_key === k) || {}).uid || k;
  return (
    <div className="overflow-x-auto">
      <ResizableTable cols={colW} className="w-full text-xs" data-testid="ev-items">
        <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground dark:bg-slate-800">
          <tr>
            <ResizableTh index={0} className="px-2 py-1 text-start">{t("s7a.panel.cols.uid")}</ResizableTh>
            <ResizableTh index={1} className="px-2 py-1 text-start">{t("s7a.panel.cols.voucher")}</ResizableTh>
            <ResizableTh index={2} className="px-2 py-1 text-start">{t("s7a.panel.cols.date")}</ResizableTh>
            <ResizableTh index={3} className="px-2 py-1 text-end">{t("s7a.panel.cols.book")}</ResizableTh>
            <ResizableTh index={4} className="px-2 py-1 text-center">{t("s7a.panel.cols.stratum")}</ResizableTh>
            <ResizableTh index={5} className="px-2 py-1 text-start">{t("s7a.panel.cols.status")}</ResizableTh>
            <ResizableTh index={6} className="px-2 py-1 text-end">{t("s7a.panel.cols.audited")}</ResizableTh>
            <ResizableTh index={7} className="px-2 py-1 text-end">{t("s7a.panel.cols.over")}</ResizableTh>
            <ResizableTh index={8} className="px-2 py-1 text-end">{t("s7a.panel.cols.under")}</ResizableTh>
            <ResizableTh index={9} className="px-2 py-1 text-end">{t("common.actions")}</ResizableTh>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <React.Fragment key={it.item_key}>
              <tr className={`border-b ${it.status === "not_applicable" ? "text-muted-foreground line-through decoration-slate-400" : ""}`}
                  data-testid={`ev-item-${it.uid || it.item_key}`}>
                <td className="px-2 py-1 font-mono" dir="ltr">{it.uid}{it.top ? <span className="ms-1 rounded bg-slate-200 px-1 text-[9px] no-underline dark:bg-slate-700">{t("s7a.panel.topTag")}</span> : null}</td>
                <td className="px-2 py-1 font-mono" dir="ltr">{it.voucher_no}</td>
                <td className="px-2 py-1">{it.entry_date ? fmtDate(it.entry_date) : ""}</td>
                <td className="px-2 py-1 text-end tabular-nums">{amt(it.book_value)}</td>
                <td className="px-2 py-1 text-center">{it.stratum ?? ""}</td>
                <td className="px-2 py-1">
                  <div data-testid={`ev-item-status-${it.uid || it.item_key}`}>{t(statusKey[it.status] || "s7a.statusPending")}
                    {it.anomaly?.is_anomaly && <span className="ms-1 rounded bg-amber-100 px-1 text-[9px] font-semibold text-amber-900">{t("s7a.anomaly")}</span>}
                  </div>
                  {it.risk_score != null && <div className="text-[10px] text-muted-foreground" data-testid={`ev-item-risk-${it.uid || it.item_key}`}>
                    {fill(t("s7a.panel.riskScore"), { s: Number(it.risk_score).toFixed(2) })}</div>}
                  {it.replacement_of && <div className="text-[10px] text-sky-800">{fill(t("s7a.panel.replacementOf"), { uid: uidOf(it.replacement_of) })}</div>}
                  {it.replaced_by && <div className="text-[10px]">{fill(t("s7a.panel.replacedBy"), { uid: uidOf(it.replaced_by) })}</div>}
                  {it.na_reason && <div className="text-[10px] no-underline">{t("s7a.panel.reason")}: {it.na_reason}</div>}
                  {it.untestable_reason && <div className="text-[10px]">{t("s7a.panel.reason")}: {it.untestable_reason}</div>}
                  {it.anomaly?.is_anomaly && it.anomaly.reason && <div className="text-[10px]" data-testid={`ev-item-anomaly-reason-${it.uid || it.item_key}`}>{t("s7a.panel.reason")}: {it.anomaly.reason}</div>}
                  {it.anomaly?.is_anomaly && <div className="text-[10px]">{t("s7a.panel.anomalyProceduresShort")}: {it.anomaly.procedures}</div>}
                </td>
                <td className="px-2 py-1 text-end tabular-nums">{it.status === "untestable" ? "0.00" : amt(it.audited_value)}</td>
                <td className="px-2 py-1 text-end tabular-nums">{it.over ? amt(it.over) : ""}</td>
                <td className="px-2 py-1 text-end tabular-nums">{it.under ? amt(it.under) : ""}</td>
                <td className="px-2 py-1 text-end">
                  {it.status !== "not_applicable" && !locked && (
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setOpen(open === it.item_key ? "" : it.item_key)}
                            data-testid={`ev-record-${it.uid || it.item_key}`}>{t("s7a.panel.record")}</Button>
                  )}
                </td>
              </tr>
              {open === it.item_key && (
                <tr><td colSpan={10} className="px-2 py-1">
                  <ItemEditor sampleId={sampleId} item={it} t={t} notASample={notASample}
                              onCancel={() => setOpen("")} onSaved={() => { setOpen(""); onSaved(); }} />
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </ResizableTable>
    </div>
  );
}
